// Status predicates for a platform action request (README §Request statuses). Pure: the polling
// composable, the badge and the detail drawer all branch on these instead of re-listing statuses.
import type { PlatformActionRequest, PlatformActionStatus } from '../models/platformAction.types'

import type { UiTone } from '@/shared/ui/types'

/**
 * `draft → pending_approval → approved → queued → executing → succeeded | failed`, plus the
 * terminals `rejected`, `expired`, `cancelled`. Polling stops at any of these.
 */
const TERMINAL_STATUSES = new Set<string>([
  'succeeded',
  'failed',
  'rejected',
  'expired',
  'cancelled',
])

/** Statuses a decision (confirm/reject) is still possible on. */
const DECIDABLE_STATUSES = new Set<string>(['draft', 'pending_approval'])

/** Cancel is only possible before execution starts (README §Request statuses). */
const CANCELLABLE_STATUSES = new Set<string>(['draft', 'pending_approval', 'approved', 'queued'])

const STATUS_TONES: Partial<Record<PlatformActionStatus, UiTone>> = {
  draft: 'neutral',
  pending_approval: 'warning',
  approved: 'info',
  queued: 'info',
  executing: 'info',
  succeeded: 'success',
  failed: 'danger',
  rejected: 'danger',
  expired: 'neutral',
  cancelled: 'neutral',
}

/** The shadow-mode marker the backend stamps while the execution kill switch is closed. */
export const EXECUTION_DISABLED_ERROR_CLASS = 'ExecutionDisabled'

export function isTerminalStatus(status: string): boolean {
  return TERMINAL_STATUSES.has(status)
}

export function isDecidable(status: string): boolean {
  return DECIDABLE_STATUSES.has(status)
}

export function isCancellable(status: string): boolean {
  return CANCELLABLE_STATUSES.has(status)
}

export function statusTone(status: string): UiTone {
  return STATUS_TONES[status as PlatformActionStatus] ?? 'neutral'
}

/**
 * True when the request terminated only because the pilot's execution kill switch is closed —
 * `failed` + `error_class: 'ExecutionDisabled'`. The UI must read this as "propose/confirm worked,
 * the platform write is switched off for the pilot", NOT as a failure the user caused.
 */
export function isExecutionDisabled(
  request: Pick<PlatformActionRequest, 'status' | 'errorClass'>,
): boolean {
  return request.status === 'failed' && request.errorClass === EXECUTION_DISABLED_ERROR_CLASS
}

/** A genuine execution failure — `failed`, but not the shadow-mode kill switch. */
export function isRealFailure(
  request: Pick<PlatformActionRequest, 'status' | 'errorClass'>,
): boolean {
  return request.status === 'failed' && !isExecutionDisabled(request)
}

/**
 * Whether to OFFER an undo. Deliberately permissive: only `succeeded` requests can be reverted at
 * all, and creates never can (their inverse does not exist), but "did the preview hold a
 * before-value" is the backend's read — it answers `409 REVERT_NOT_ELIGIBLE` when it did not.
 * Re-deriving that here would mean copying guardrail logic this module does not own.
 */
export function isRevertable(request: { status: string; actionType: string }): boolean {
  return request.status === 'succeeded' && !request.actionType.endsWith('.create')
}
